<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcEmployeeFieldset',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    employee: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.employee,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('employee');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_flme2b1gc =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_u1aw9bvzl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_flme2b1gc,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_u1aw9bvzl],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_u1aw9bvzl,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_u1aw9bvzl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_u1aw9bvzl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="name"
      :label="pgettext('Employee', 'Name')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="name"
        v-model="employee.name"
        :value="employee.name"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (employee.name =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
        autofocus
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="job_title"
      :label="pgettext('Employee', 'Job Title')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="job_title"
        v-model="employee.job_title"
        :value="employee.job_title"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (employee.job_title =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="phone"
      :label="pgettext('Employee', 'Phone Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="phone"
        v-model="employee.phone"
        :value="employee.phone"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (employee.phone =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="mobile"
      :label="pgettext('Employee', 'Mobile Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="mobile"
        v-model="employee.mobile"
        :value="employee.mobile"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (employee.mobile =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="email"
      :label="pgettext('Employee', 'Email')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="email"
        v-model="employee.email"
        :value="employee.email"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (employee.email =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="fax"
      :label="pgettext('Employee', 'Fax')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="fax"
        v-model="employee.fax"
        :value="employee.fax"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (employee.fax =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="notes"
      :label="pgettext('Employee', 'Notes')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea
        name="notes"
        v-model="employee.notes"
        :value="employee.notes"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (employee.notes =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
